import {
  Building2,
  CalendarDays,
  CircleDollarSign,
  Download,
  FileText,
  ShieldCheck,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import {
  RESIDENT_COMPANY_ROLES,
  RESIDENT_LIBERTY_ROLES,
} from "../../constants/residentConstants";
import { lookupConstantLabel } from "../../utils/residentListConstants";
import {
  getReimbursementId,
  shouldShowReimbursementDetailChrome,
} from "../../utils/residentDecoupling";
import {
  resolveNrlsdDate,
  resolveReimbursementRequestDate,
  shouldShowLeaseApprovalHeaderCard,
} from "../../utils/residentHeaderStatus";
import {
  REIMBURSEMENT_HEADER_LABELS,
  getReimbursementWorkflowProgressPillClassName,
  getReimbursementWorkflowStatusPillClassName,
  resolveContractPurchaseDate,
  getReimbursementDocumentTriggerPillClassName,
  resolveHeaderDocumentTriggerLabel,
  resolveHeaderLastUpdatedValue,
  resolveHeaderProgressKey,
} from "@/features/reimbursements/utils/reimbursementHeaderSummaryFields";
import {
  canNavigateApplicantCardToApplication,
  canShowResidentDownloadAccessConfirmation,
  canShowViewApplicationLink,
  canShowViewReimbursementLink,
} from "../../utils/residentValidation";
import { parseDisplayDate } from "../../utils/residentDateUtils";
import { resolveActiveCoResident } from "../../utils/resolveActiveCoResident";
import type { ResidentHeaderSummaryProps } from "../../types";
import {
  reimbursementRentalProgressLabel,
  reimbursementStatusLabel,
} from "../../utils/residentListStatusStyles";
import { formatApplicantIdSegment } from "../../utils/applicantIdFormat";
import { cn } from "@/lib/utils/helpers";
import {
  RESIDENT_HEADER_TONE_STYLES,
  residentHeaderDangerActionClass,
  residentHeaderOutlineActionClass,
  residentHeaderPrimaryActionClass,
} from "../../constants/headerStyles";
import { ROUTE_BUILDERS } from "@/config/routes";

function formatDate(value?: unknown, yearOnly = false): string {
  const date = parseDisplayDate(value);
  if (!date) return "";
  if (yearOnly) return String(date.getFullYear());
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
}

function formatDateTime(value?: unknown): string {
  const date = parseDisplayDate(value);
  if (!date) return "-";
  return date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function clean(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function listedBadge(listed: unknown, hasName: boolean) {
  if (!hasName) return null;
  const active = listed === 1 || listed === "1";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-none",
        active
          ? "border-[#86efac] bg-[#e6f4ea] text-[#15803d]"
          : "border-slate-200 bg-slate-100 text-slate-600",
      )}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- action builder co-located with the header it feeds
export function buildResidentHeaderActions({
  pageType = "resident-detail",
  applicationData,
  residentDetail,
  routeApplicantId,
  routeUserId,
  roleName,
  onAccessConfirmation,
  accessConfirmationLoading = false,
  hasPolicy,
}: Pick<
  ResidentHeaderSummaryProps,
  | "pageType"
  | "applicationData"
  | "residentDetail"
  | "routeApplicantId"
  | "routeUserId"
  | "roleName"
  | "onAccessConfirmation"
  | "accessConfirmationLoading"
  | "hasPolicy"
>) {
  const applicationDetailApplicantId =
    applicationData?.applicant_id != null &&
    String(applicationData.applicant_id).trim() !== ""
      ? String(applicationData.applicant_id)
      : routeApplicantId?.trim() || "";

  // Angular: progress_status from co-resident `applicationData`; status/rent/balance from root `applicationAllData`.
  const activeCo = resolveActiveCoResident(applicationData, routeApplicantId);
  const coProgress = (activeCo as { progress_status?: unknown } | undefined)
    ?.progress_status;
  const showAccessConfirmation = canShowResidentDownloadAccessConfirmation({
    progress_status: coProgress ?? applicationData?.progress_status,
    application_status: applicationData?.application_status,
    rent_added: applicationData?.rent_added,
    balance_due: applicationData?.balance_due,
  });
  const showApplicationLink =
    Boolean(applicationDetailApplicantId) &&
    hasPolicy &&
    canShowViewApplicationLink(roleName, hasPolicy);
  const reimbursementId = getReimbursementId(
    residentDetail as Record<string, unknown> | null | undefined,
  );
  const reimbursementUserId = clean(
    routeUserId || (applicationData?.fk_user_id as string | number | undefined),
  );
  const showReimbursementLink =
    pageType === "resident-detail" &&
    Boolean(applicationDetailApplicantId) &&
    Boolean(reimbursementUserId) &&
    reimbursementId !== undefined &&
    hasPolicy &&
    canShowViewReimbursementLink(roleName, hasPolicy);
  const reimbursementHref = showReimbursementLink
    ? `${ROUTE_BUILDERS.DEFAULT_DETAIL(
        formatApplicantIdSegment(applicationDetailApplicantId),
        reimbursementUserId,
        reimbursementId,
      )}#default`
    : "";

  if (!showAccessConfirmation && !showApplicationLink && !showReimbursementLink) return null;

  return (
    <div
      className="flex w-full min-w-0 max-w-full flex-row flex-wrap items-center justify-end gap-2"
      data-testid="resident-header-summary-actions-block"
    >
      {showAccessConfirmation ? (
        <button
          type="button"
          onClick={onAccessConfirmation}
          disabled={accessConfirmationLoading}
          data-testid="resident-header-summary-access-confirmation-button"
          className={residentHeaderOutlineActionClass}
        >
          <Download className="h-4 w-4 shrink-0" aria-hidden="true" />
          {accessConfirmationLoading
            ? "Opening…"
            : "Download Access Confirmation"}
        </button>
      ) : null}
      {showApplicationLink ? (
        <a
          href={`/application/${formatApplicantIdSegment(applicationDetailApplicantId)}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="resident-header-summary-application-link"
          className={residentHeaderPrimaryActionClass}
        >
          <FileText className="h-4 w-4 shrink-0" aria-hidden="true" />
          View Application
        </a>
      ) : null}
      {showReimbursementLink ? (
        <a
          href={reimbursementHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="resident-header-summary-reimbursement-link"
          className={residentHeaderDangerActionClass}
        >
          <CircleDollarSign className="h-4 w-4 shrink-0" aria-hidden="true" />
          View Reimbursement
        </a>
      ) : null}
    </div>
  );
}

/**
 * Resident detail summary — profile hero + PROPERTY / CONTRACT / LEASE & APPROVAL cards
 * (and optional reimbursement column when Skip/Eviction).
 */
export function ResidentHeaderSummary({
  pageType = "resident-detail",
  applicationData,
  residentDetail,
  statusLabelMaps,
  routeApplicantId,
  routeUserId,
  roleName,
  canManageExcludeAuto = false,
  onExcludeAuto,
  onAccessConfirmation,
  accessConfirmationLoading = false,
  hasPolicy,
  showActions = true,
  formValues = null,
}: ResidentHeaderSummaryProps) {
  const [pendingExcludeAuto, setPendingExcludeAuto] = useState<boolean | null>(
    null,
  );
  const residentDefault = residentDetail?.resident_default;
  const isCompanyRole = RESIDENT_COMPANY_ROLES.includes(roleName);
  const isLibertyRole = RESIDENT_LIBERTY_ROLES.includes(roleName);
  const property = applicationData?.property;
  const appRecord = applicationData as Record<string, unknown> | null;

  // Angular header binds Lease End / Request Date / ACTIVE|CLOSED visibility to the live form.
  const liveLeaseEndDate =
    formValues?.lease_end_date || residentDefault?.lease_end_date;
  const liveRequestDateSource = {
    rental_status_date:
      formValues?.rental_status_date || residentDefault?.rental_status_date,
    claim_request_date: (
      residentDefault as { claim_request_date?: unknown } | null | undefined
    )?.claim_request_date,
  };

  const showReimbursementSummary = shouldShowReimbursementDetailChrome(
    pageType,
    residentDefault?.reimbursement_type,
  );
  const showLeaseApprovalCard = shouldShowLeaseApprovalHeaderCard({
    isCompanyRole,
    isLibertyRole,
    showReimbursementSummary,
  });
  const overviewCardCount =
    2 + (showLeaseApprovalCard ? 1 : 0) + (showReimbursementSummary ? 1 : 0);

  const lastUpdatedDisplay = formatDateTime(
    resolveHeaderLastUpdatedValue(residentDefault, appRecord),
  );

  const liveAdjustedNotice =
    formValues?.adjusted_notice || String(residentDefault?.adjusted_notice ?? "");
  const liveMoveOutDate =
    formValues?.moveout_date || String(residentDefault?.moveout_date ?? "");

  const reimbursementRentalStatus = String(
    residentDefault?.reimbursement_rental_status ?? "",
  ).trim();
  const reimbursementRentalProgress = String(
    residentDefault?.reimbursement_rental_progress ?? "",
  ).trim();
  const headerReimbursementStatusLabel = reimbursementStatusLabel(
    reimbursementRentalStatus,
    statusLabelMaps,
  );
  const headerReimbursementProgressLabel = reimbursementRentalProgressLabel(
    reimbursementRentalProgress,
    statusLabelMaps,
  );
  const headerDocumentsLabel = resolveHeaderDocumentTriggerLabel({
    documentStatus: residentDefault?.document_status,
  });

  const headerProgressKey = resolveHeaderProgressKey({
    rentalProgress: residentDefault?.reimbursement_rental_progress,
    progressLabelMaps:
      statusLabelMaps?.reimbursement_rental_progress ??
      statusLabelMaps?.resident_rental_progress,
  });

  const companyName = clean(property?.company_name);
  const propertyName = clean(property?.property_name);

  const firstName =
    clean(applicationData?.first_name) ||
    clean(applicationData?.user_data?.first_name);
  const lastName =
    clean(applicationData?.last_name) ||
    clean(applicationData?.user_data?.last_name);
  const applicantId = formatApplicantIdSegment(
    applicationData?.applicant_id ?? routeApplicantId ?? "",
  );
  const progressKey = String(residentDefault?.rental_progress || "").trim();
  const progressLabel =
    lookupConstantLabel(statusLabelMaps?.resident_rental_progress, progressKey) ||
    lookupConstantLabel(statusLabelMaps?.reimbursement_rental_progress, progressKey) ||
    clean(residentDefault?.rental_progress) ||
    clean(applicationData?.rental_progress);

  // Build residents array
  const coMap = appRecord?.co_resident_application || {};
  const allResidents = Object.values(coMap) as Array<Record<string, unknown>>;

  if (allResidents.length === 0 && appRecord) {
    allResidents.push({
      applicant_id: applicantId,
      is_primary: 1,
      user_data: {
        first_name: firstName,
        last_name: lastName,
      },
    });
  }

  allResidents.sort((a, b) => {
    if (a.is_primary) return -1;
    if (b.is_primary) return 1;
    return 0;
  });

  // Angular co-resident strip: liberty roles get a name link to `/application/:id`
  // (not gated by application_detail_view — that policy is only for "View Application").
  const canOpenApplicationFromCard =
    canNavigateApplicantCardToApplication(roleName);

  const purchaseDate = formatDate(
    resolveContractPurchaseDate(applicationData ?? undefined),
  );
  const vintageYear = formatDate(applicationData?.contract_start_date, true);
  const contractStart = formatDate(applicationData?.contract_start_date);
  const contractEnd = formatDate(applicationData?.contract_end_date);

  const actions = showActions
    ? buildResidentHeaderActions({
        pageType,
        applicationData,
        residentDetail,
        routeApplicantId,
        routeUserId,
        roleName,
        onAccessConfirmation,
        accessConfirmationLoading,
        hasPolicy,
      })
    : null;

  return (
    <section className="space-y-4" data-testid="resident-header-summary">
      {/* Profile hero */}
      <div
        className="rounded-2xl border border-[#dbe7f3] bg-white p-4 shadow-[0_4px_12px_rgba(15,23,42,0.06)] sm:p-5"
        data-testid="resident-header-summary-profile"
      >
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between xl:gap-6">
          <div className="flex flex-wrap items-center gap-4 min-w-0 flex-1">
            {allResidents.map((res) => {
              const resId = formatApplicantIdSegment(
                (res.applicant_id as string | number | undefined) ?? "",
              );
              const fName = clean(
                res.first_name ||
                  (res.user_data as Record<string, unknown>)?.first_name,
              );
              const lName = clean(
                res.last_name ||
                  (res.user_data as Record<string, unknown>)?.last_name,
              );
              const resFullName = `${fName} ${lName}`.trim() || "Resident";
              const applicationHref = resId ? `/application/${resId}` : "";
              const showAppLink =
                Boolean(applicationHref) && canOpenApplicationFromCard;
              const isActive = resId === applicantId;

              const cardClassName = cn(
                "min-w-[200px] max-w-full rounded-2xl border bg-white px-4 py-3.5 shadow-sm transition-colors",
                isActive
                  ? "border-[#1597ff] ring-1 ring-[#1597ff]"
                  : "border-slate-200",
                showAppLink &&
                  "cursor-pointer hover:border-[#1597ff]/hover:bg-slate-50/60",
              );

              const cardBody = (
                <>
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
                    <h2
                      className={cn(
                        "m-0 truncate text-sm font-bold text-slate-900",
                        showAppLink && "hover:underline",
                      )}
                      data-testid={`resident-header-summary-name-${resId}`}
                      title={resFullName}
                    >
                      {resFullName}
                    </h2>
                    {isActive && progressLabel ? (
                      <span
                        className="inline-flex max-w-full items-center whitespace-nowrap rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-bold text-blue-700 ring-1 ring-inset ring-blue-200"
                        data-testid={`resident-header-summary-progress-badge-${resId}`}
                        title={progressLabel}
                      >
                        {progressLabel}
                      </span>
                    ) : null}
                  </div>
                  <p
                    className="m-0 mt-1 text-xs font-semibold text-slate-500"
                    data-testid={`resident-header-summary-applicant-id-${resId}`}
                  >
                    Applicant {resId || "—"}
                  </p>
                </>
              );

              if (showAppLink) {
                return (
                  <a
                    key={resId}
                    href={applicationHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      cardClassName,
                      "block no-underline text-inherit",
                    )}
                    data-testid={`resident-header-summary-applicant-card-${resId}`}
                    title={`Open application ${resId}`}
                  >
                    {cardBody}
                  </a>
                );
              }

              return (
                <div
                  key={resId}
                  className={cardClassName}
                  data-testid={`resident-header-summary-applicant-card-${resId}`}
                >
                  {cardBody}
                </div>
              );
            })}
          </div>
          {actions ? <div className="shrink-0 sm:pt-1">{actions}</div> : null}
        </div>
      </div>

      {/* Info cards */}
      <div
        className={cn(
          "grid grid-cols-1 gap-3 md:grid-cols-2",
          overviewCardCount >= 4
            ? "xl:grid-cols-4"
            : overviewCardCount === 3
              ? "xl:grid-cols-3"
              : "xl:grid-cols-2",
        )}
      >
        <InfoCard
          title="PROPERTY"
          icon={<Building2 className="h-4 w-4" aria-hidden="true" />}
          tone="blue"
          testId="resident-header-summary-property-block"
        >
          <SummaryRow
            label={REIMBURSEMENT_HEADER_LABELS.company}
            testId="resident-header-summary-mgmt-company"
            value={
              <span className="inline-flex flex-wrap items-center justify-end gap-1.5">
                <span>{companyName || "-"}</span>
                {listedBadge(property?.company_listed, !!companyName)}
              </span>
            }
          />
          <SummaryRow
            label={REIMBURSEMENT_HEADER_LABELS.property}
            testId="resident-header-summary-property"
            value={
              <span className="inline-flex flex-wrap items-center justify-end gap-1.5">
                <span>{propertyName || "-"}</span>
                {listedBadge(property?.property_listed, !!propertyName)}
              </span>
            }
          />
          <SummaryRow
            label={REIMBURSEMENT_HEADER_LABELS.propertySupportTeam}
            value={clean(property?.customer_success_mgr)}
            testId="resident-header-summary-support-team"
          />
          <SummaryRow
            label={REIMBURSEMENT_HEADER_LABELS.lastUpdated}
            value={lastUpdatedDisplay}
            testId="resident-header-summary-last-updated-pm"
          />
          {showReimbursementSummary && residentDefault?.exclude_auto && canManageExcludeAuto ? (
            <>
              <label
                className="inline-flex items-center gap-2 text-[13px] text-slate-700"
                data-testid="resident-header-summary-exclude-auto"
              >
                <input
                  type="checkbox"
                  data-testid="resident-header-summary-exclude-auto-checkbox"
                  checked={
                    pendingExcludeAuto !== null
                      ? pendingExcludeAuto
                      : residentDefault.exclude_auto === "1"
                  }
                  onChange={(event) =>
                    setPendingExcludeAuto(event.target.checked)
                  }
                />
                Exclude Auto Function
              </label>

              {pendingExcludeAuto !== null ? (
                <ConfirmDialog
                  isOpen
                  title="Auto Expire/Deny Function"
                  message={`Do you want to ${pendingExcludeAuto ? "exclude" : "include"} this Reimbursement ${pendingExcludeAuto ? "from" : "in"} the auto expire/deny function?`}
                  primaryLabel="Yes"
                  secondaryLabel="No"
                  onPrimary={() => {
                    const target = pendingExcludeAuto;
                    setPendingExcludeAuto(null);
                    onExcludeAuto?.(target);
                  }}
                  onSecondary={() => setPendingExcludeAuto(null)}
                  onDismiss={() => setPendingExcludeAuto(null)}
                  overlayDataTestId="exclude-auto-confirm-dialog"
                  primaryButtonDataTestId="exclude-auto-confirm-yes-button"
                  secondaryButtonDataTestId="exclude-auto-confirm-no-button"
                />
              ) : null}
            </>
          ) : null}
        </InfoCard>

        <InfoCard
          title="CONTRACT"
          icon={<CalendarDays className="h-4 w-4" aria-hidden="true" />}
          tone="purple"
          testId="resident-header-summary-contract-block"
        >
          <SummaryRow
            label={REIMBURSEMENT_HEADER_LABELS.contractPurchaseDate}
            value={purchaseDate}
            testId="resident-header-summary-contract-paid-date"
          />
          <SummaryRow
            label={REIMBURSEMENT_HEADER_LABELS.vintageYear}
            value={vintageYear}
            testId="resident-header-summary-vintage-year"
          />
          <SummaryRow
            label={REIMBURSEMENT_HEADER_LABELS.contractStart}
            value={contractStart}
            testId="resident-header-summary-contract-start"
          />
          <SummaryRow
            label={REIMBURSEMENT_HEADER_LABELS.contractEnd}
            value={contractEnd}
            testId="resident-header-summary-contract-end"
          />
        </InfoCard>

        {showLeaseApprovalCard ? (
          <InfoCard
            title="LEASE & APPROVAL"
            icon={<ShieldCheck className="h-4 w-4" aria-hidden="true" />}
            tone="orange"
            testId="resident-header-summary-lease-block"
          >
            {!isCompanyRole ? (
              <SummaryRow
                label={REIMBURSEMENT_HEADER_LABELS.leaseEndDate}
                value={formatDate(liveLeaseEndDate)}
                testId="resident-header-summary-lease-end"
              />
            ) : null}
            {showReimbursementSummary ? (
              <SummaryRow
                label={REIMBURSEMENT_HEADER_LABELS.nrlsd}
                value={formatDate(resolveNrlsdDate(residentDetail)) || "-"}
                testId="resident-header-summary-nrlsd"
              />
            ) : null}
            {isLibertyRole ? (
              <SummaryRow
                label={REIMBURSEMENT_HEADER_LABELS.applicationApprover}
                value={clean(applicationData?.application_approver)}
                testId="resident-header-summary-application-approver"
              />
            ) : null}
          </InfoCard>
        ) : null}

        {showReimbursementSummary ? (
          <InfoCard
            title="REIMBURSEMENT"
            icon={<CircleDollarSign className="h-4 w-4" aria-hidden="true" />}
            tone="red"
            testId="resident-header-summary-reimbursement-block"
          >
            <SummaryRow
              label={REIMBURSEMENT_HEADER_LABELS.requestDate}
              value={formatDate(
                resolveReimbursementRequestDate(liveRequestDateSource),
              )}
              testId="resident-header-summary-rental-status-date"
            />
            <SummaryRow
              label={REIMBURSEMENT_HEADER_LABELS.startDate}
              value={formatDate(liveAdjustedNotice) || "-"}
              testId="resident-header-summary-start-date"
            />
            <SummaryRow
              label={REIMBURSEMENT_HEADER_LABELS.moveOutDate}
              value={formatDate(liveMoveOutDate) || "-"}
              testId="resident-header-summary-move-out-date"
            />
            <SummaryRow
              label={REIMBURSEMENT_HEADER_LABELS.status}
              value={
                <span
                  className={getReimbursementWorkflowStatusPillClassName(
                    reimbursementRentalStatus,
                  )}
                >
                  {headerReimbursementStatusLabel || "—"}
                </span>
              }
              testId="resident-header-summary-claim-status"
            />
            {reimbursementRentalProgress ? (
              <SummaryRow
                label={REIMBURSEMENT_HEADER_LABELS.progress}
                value={
                  <span
                    className={getReimbursementWorkflowProgressPillClassName(
                      headerProgressKey,
                    )}
                  >
                    {headerReimbursementProgressLabel}
                  </span>
                }
                testId="resident-header-summary-progress"
              />
            ) : null}
            <SummaryRow
              label={REIMBURSEMENT_HEADER_LABELS.documents}
              value={
                <span
                  className={getReimbursementDocumentTriggerPillClassName(
                    residentDefault?.document_status,
                  )}
                >
                  {headerDocumentsLabel}
                </span>
              }
              testId="resident-header-summary-document-status"
            />
            <SummaryRow
              label={REIMBURSEMENT_HEADER_LABELS.review}
              value={clean(residentDefault?.review_decision) || "-"}
              testId="resident-header-summary-review-decision"
            />
          </InfoCard>
        ) : null}
      </div>
    </section>
  );
}

function InfoCard({
  title,
  icon,
  tone,
  testId,
  children,
}: {
  title: string;
  icon: ReactNode;
  tone: keyof typeof RESIDENT_HEADER_TONE_STYLES;
  testId?: string;
  children: ReactNode;
}) {
  const styles = RESIDENT_HEADER_TONE_STYLES[tone];
  return (
    <div
      className={cn(
        "rounded-2xl border p-4 shadow-[0_2px_8px_rgba(15,23,42,0.04)]",
        styles.card,
      )}
      data-testid={testId}
    >
      <div className="mb-3">
        <div className="mb-2.5 flex items-center gap-2">
          <span
            className={cn(
              "inline-flex h-8 w-8 items-center justify-center rounded-lg",
              styles.iconWrap,
            )}
          >
            {icon}
          </span>
          <h3
            className={cn("m-0 text-xs font-bold tracking-wider", styles.title)}
          >
            {title}
          </h3>
        </div>
        <div className={cn("h-px w-full", styles.rule)} aria-hidden="true" />
      </div>
      <div className="grid gap-2.5">{children}</div>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  testId,
}: {
  label: string;
  value?: ReactNode;
  testId?: string;
}) {
  const display =
    value === null || value === undefined || value === ""
      ? "-"
      : typeof value === "string" || typeof value === "number"
        ? String(value)
        : value;

  return (
    <div
      className="flex min-w-0 items-start justify-between gap-3 text-[13px] leading-[1.35]"
      data-testid={testId}
    >
      <span className="shrink-0 font-medium text-slate-500">{label}</span>
      <span className="min-w-0 text-right font-medium text-slate-800">
        {display}
      </span>
    </div>
  );
}
